import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import dayjs from 'dayjs';
import logger from './logging/logger.js';
import router from '@/router/index';
import posthog from '@/composables/usePostHog.js';
import { reportError } from '@/services/errorReporting';
import { broadcastSessionCleared, broadcastSessionUpdated, clearPersistedAuthArtifacts, emitAuthSessionCleared, emitAuthSessionUpdated, parseStoredJson, updateSessionTokens } from '@/util/authSession';

const BASE_URL = `${import.meta.env['VITE_API_BASE_URL']}/api/v1`;

const apiClient = axios.create({
	baseURL: BASE_URL,
	withCredentials: false
});

const refreshClient = axios.create({ baseURL: BASE_URL });

const getStoredRoles = (key: string): string[] => parseStoredJson<string[]>(localStorage.getItem(key), []);

const getImpersonationMode = (): 'none' | 'restricted' | 'unrestricted' | 'invalid' => {
	try {
		const impersonationState = sessionStorage.getItem('impersonation_state');
		if (!impersonationState) return 'none';

		const state = JSON.parse(impersonationState) as { isImpersonating?: boolean };
		if (!state.isImpersonating) return 'none';

		const originalRoles = getStoredRoles('original_user_roles');
		if (originalRoles.some((role) => ['Developer', 'Evergreen Sports'].includes(role))) {
			return 'unrestricted';
		}
		if (originalRoles.includes('Referee Administrator')) {
			return 'restricted';
		}
		return 'invalid';
	} catch {
		return 'none';
	}
};

export const isReadOnlyImpersonation = () => getImpersonationMode() === 'restricted';

export const isImpersonating = () => {
	const impersonationState = parseStoredJson<{ isImpersonating?: boolean } | null>(sessionStorage.getItem('impersonation_state'), null);
	return impersonationState?.isImpersonating === true;
};

const ALWAYS_ALLOWED_IMPERSONATION_ENDPOINTS = ['/auth/exit-impersonation', '/auth/impersonation-status'];
const RESTRICTED_IMPERSONATION_BLOCKED_ENDPOINTS = ['/ledger', '/payments', '/user/settings', '/addresses', '/users/edit', '/users/saveAll'];
const RESTRICTED_IMPERSONATION_BLOCKED_FEE_SCHEDULE_PATHS = ['/fee/schedule/save', '/fee/schedule/edit', '/fee/schedule/delete'];

const isBlockedDuringRestrictedImpersonation = (config: InternalAxiosRequestConfig) => {
	const url = config.url || '';
	const method = String(config.method || 'get').toLowerCase();
	const isBlockedEndpoint = RESTRICTED_IMPERSONATION_BLOCKED_ENDPOINTS.some((endpoint) => url.includes(endpoint));
	if (isBlockedEndpoint) {
		return true;
	}

	if (['post', 'patch', 'delete'].includes(method)) {
		return RESTRICTED_IMPERSONATION_BLOCKED_FEE_SCHEDULE_PATHS.some((endpoint) => url.includes(endpoint));
	}

	return false;
};

let isRefreshing = false;
let refreshQueue: Array<{
	resolve: (token: string) => void;
	reject: (error: unknown) => void;
}> = [];

function addRefreshSubscriber(resolve: (token: string) => void, reject: (error: unknown) => void) {
	refreshQueue.push({ resolve, reject });
}

function onRefreshed(token: string) {
	refreshQueue.forEach(({ resolve }) => {
		resolve(token);
	});
	refreshQueue = [];
}

function onRefreshFailed(error: unknown) {
	refreshQueue.forEach(({ reject }) => {
		reject(error);
	});
	refreshQueue = [];
}

/**
 * Exchanges the current refresh token for an access token with the latest
 * server-side permissions. This is also used after an invitation grants a
 * role during an already-authenticated session.
 */
export async function refreshAuthSession(): Promise<string> {
	if (isRefreshing) {
		return new Promise<string>((resolve, reject) => {
			addRefreshSubscriber(resolve, reject);
		});
	}

	isRefreshing = true;
	try {
		const refreshToken = sessionStorage.getItem('refresh_token');
		if (!refreshToken) {
			throw new Error('Missing refresh token');
		}

		const { data } = await refreshClient.post<{ accessToken?: string; refreshToken?: string | null }>('/auth/refresh-token', { refreshToken });
		if (!data.accessToken) {
			throw new Error('Refresh response missing access token');
		}

		updateSessionTokens(data.accessToken, data.refreshToken ?? null);
		emitAuthSessionUpdated('local');
		broadcastSessionUpdated();
		onRefreshed(data.accessToken);
		return data.accessToken;
	} catch (error: unknown) {
		onRefreshFailed(error);
		throw error;
	} finally {
		isRefreshing = false;
	}
}

async function redirectToLogin() {
	try {
		await router.push({ name: 'login' });
	} catch {
		window.location.href = '/login';
	}
}

export async function clearAuthSessionAndRedirectToLogin() {
	clearPersistedAuthArtifacts();
	emitAuthSessionCleared('local');
	broadcastSessionCleared();
	await redirectToLogin();
}

const isAuthRoute = (url = '') => url.includes('/login') || url.includes('/register');
const isUntrackedAuthRoute = (url: string | null | undefined): boolean => {
	const path = (url ?? '').split(/[?#]/u, 1)[0] ?? '';
	return /\/(?:auth\/)?(?:login|logout)\/?$/iu.test(path);
};

declare module 'axios' {
	interface AxiosRequestConfig {
		/** Used by screens that render their own errors instead of the app-wide error page. */
		skipErrorRedirect?: boolean;
	}
}

interface SerializableError {
	name: string;
	code: string | null;
	message: string;
	status: number | null;
	url: string | null;
	method: string | null;
}

function toSerializableError(error: unknown): SerializableError {
	const axiosError = axios.isAxiosError(error) ? error : null;
	const nativeError = error instanceof Error ? error : null;
	return {
		name: axiosError?.name || nativeError?.name || 'Error',
		code: axiosError?.code ?? null,
		message: axiosError?.message || nativeError?.message || 'Unknown error',
		status: axiosError?.response?.status ?? null,
		url: axiosError?.config?.url ?? null,
		method: axiosError?.config?.method?.toUpperCase() ?? null
	};
}

function toError(error: unknown, fallbackMessage: string): Error {
	if (error instanceof Error) return error;
	if (typeof error === 'string' && error.trim()) return new Error(error);
	return new Error(fallbackMessage);
}

apiClient.interceptors.request.use(
	(config) => {
		// Preserve the CSV preview and edits when upload lookups or submissions fail.
		if (router.currentRoute.value.name === 'bulk-upload') {
			config.skipErrorRedirect = true;
		}

		const token = sessionStorage.getItem('auth_token');
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}

		const url = config.url || '';
		const isAlwaysAllowedEndpoint = ALWAYS_ALLOWED_IMPERSONATION_ENDPOINTS.some((endpoint) => url.includes(endpoint));
		const impersonationMode = getImpersonationMode();

		if (impersonationMode === 'invalid' && !isAlwaysAllowedEndpoint) {
			const error = new Error('This impersonation session is no longer valid. Exit impersonation and try again.');
			(error as Error & { isImpersonationBlock?: boolean }).isImpersonationBlock = true;
			return Promise.reject(error);
		}

		if (impersonationMode === 'restricted' && !isAlwaysAllowedEndpoint && isBlockedDuringRestrictedImpersonation(config)) {
			logger.warn('[Impersonation] Blocked request during restricted admin impersonation', {
				method: config.method,
				url: config.url
			});

			posthog.capture('impersonation_action_blocked', {
				method: config.method,
				url: config.url,
				endpoint: config.url?.split('?')[0]
			});

			window.dispatchEvent(
				new CustomEvent('impersonation-action-blocked', {
					detail: {
						method: config.method,
						url: config.url
					}
				})
			);

			const error = new Error('This action is not available during admin impersonation.');
			(error as Error & { isImpersonationBlock?: boolean }).isImpersonationBlock = true;
			return Promise.reject(error);
		}

		logger.api(`[Request] ${config.method?.toUpperCase()} ${config.url}`, {
			params: config.params as unknown,
			data: config.url?.split('?')[0]?.replace(/\/$/u, '').endsWith('/auth/change-password') ? '[REDACTED]' : (config.data as unknown),
			timestamp: dayjs().format('YYYY-MM-DD HH:mm:ss.SSS')
		});

		return config;
	},
	(error: unknown) => {
		const serializableError = toSerializableError(error);
		logger.error('[Request Error]', serializableError);
		if (!isUntrackedAuthRoute(serializableError.url)) {
			reportError(error, {
				source: 'api',
				domain: 'http',
				operation: 'request_setup',
				handled: true
			});
		}
		throw toError(error, 'Failed to configure API request');
	}
);

apiClient.interceptors.response.use(
	(response) => {
		logger.api(`[Response] ${response.config.url}`, {
			status: response.status,
			data: response.data as unknown,
			timestamp: dayjs().format('YYYY-MM-DD HH:mm:ss.SSS')
		});
		return response;
	},
	async (error: AxiosError) => {
		const { config, response, message } = error;
		const originalRequest = config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

		if (!response) {
			logger.error('[Network Error]', { message });
			if (!isUntrackedAuthRoute(originalRequest?.url)) {
				reportError(error, {
					source: 'api',
					domain: 'network',
					operation: 'request_failed',
					handled: true,
					http: {
						method: originalRequest?.method,
						url: originalRequest?.url
					}
				});
			}

			if (!isAuthRoute(originalRequest?.url) && !originalRequest?.skipErrorRedirect) {
				const userRoles = parseStoredJson<string[]>(localStorage.getItem('user_roles'), []);
				await router.push({ name: userRoles.length > 0 ? 'Private Error' : 'Public Error' });
			}
			throw error;
		}

		const responseRequest: InternalAxiosRequestConfig & { _retry?: boolean } = config ?? response.config;
		if (isUntrackedAuthRoute(responseRequest.url)) {
			logger.warn('[Auth request failed] Login/logout telemetry is disabled.', {
				method: responseRequest.method,
				status: response.status
			});
			throw error;
		}

		if (response.status === 401 && isImpersonating()) {
			logger.warn('[Impersonation Session Expired] Token expired during impersonation');
			window.dispatchEvent(new CustomEvent('impersonation-session-expired'));

			const impersonationExpiryError = new Error('Impersonation session expired');
			(impersonationExpiryError as Error & { isImpersonationExpiry?: boolean }).isImpersonationExpiry = true;
			throw impersonationExpiryError;
		}

		if (response.status === 401 && !responseRequest._retry) {
			if (isAuthRoute(responseRequest.url)) {
				logger.warn('[Auth Error on Login/Register] No Refresh Attempt.');
				throw error;
			}

			responseRequest._retry = true;
			let refreshedAccessToken: string;

			try {
				refreshedAccessToken = await refreshAuthSession();
			} catch (refreshError: unknown) {
				const refreshErrorData = toSerializableError(refreshError);
				logger.error('[Token Refresh Failed]', refreshErrorData);
				reportError(refreshError, {
					source: 'api',
					domain: 'authentication',
					operation: 'refresh_session',
					handled: true,
					http: {
						method: refreshErrorData.method,
						url: refreshErrorData.url,
						status: refreshErrorData.status
					},
					details: {
						code: refreshErrorData.code
					}
				});
				await clearAuthSessionAndRedirectToLogin();
				throw toError(refreshError, 'Failed to refresh authentication session');
			}

			logger.api('[Token Refresh Success]', { accessToken: '***' });
			responseRequest.headers.Authorization = `Bearer ${refreshedAccessToken}`;
			return apiClient(responseRequest);
		}

		logger.error(`[Response Error] ${response.config.url || 'Unknown URL'}`, {
			status: response.status,
			message: error.message,
			data: response.data
		});
		if (!isUntrackedAuthRoute(responseRequest.url)) {
			const requestId: unknown = response.headers['x-request-id'];
			reportError(error, {
				source: 'api',
				domain: 'http',
				operation: 'response_error',
				handled: true,
				http: {
					method: responseRequest.method,
					url: responseRequest.url,
					status: response.status,
					statusText: response.statusText,
					requestId: typeof requestId === 'string' ? requestId : null
				}
			});
		}
		throw error;
	}
);

export default apiClient;
